import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Form,
  Icons,
  List,
  Select,
  Space,
  Table,
  TextField,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  GetListResponse,
  LogicalFilter,
  CrudFilters,
  useCustomMutation,
} from '@pankod/refine-core'
import Head from 'next/head'
import React, { useEffect } from 'react'

import {
  APPROVAL_STATUS,
  CalonPesertaDidikRequest,
  CalonPesertaDidikResponse,
} from '@resources/pendaftaran-diklat/types'
import SelectFilterAsync from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/SelectAsync'
import Filter, { TFilterForm } from '@components/ContentFilter'
import useTableWithMeta from 'src/hooks/useTableWithMeta'
import { TCommonError } from 'src/interfaces/common'
import ApprovalStatus from '@resources/pendaftaran-diklat/block/ApprovalStatus'
import {
  SelectCityOptions,
  SelectDistrictOptions,
  SelectProvinceOptions,
} from '@resources/basic-ppg-management/lptk/blocks/SelectLPTKDataOptions'
import ModalFormUpdateProgramLocal, {
  useUpdateProgramLocalModal,
} from '@resources/pendaftaran-diklat/penyetujuan-peserta-didik/block/ModalFormUpdateProgramLocal'
import ModalRejectReason, {
  useRejectReasonModal,
} from '@resources/pendaftaran-diklat/penyetujuan-peserta-didik/block/ModalRejectReason'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import ButtonAsync from '@resources/pendaftaran-diklat/block/ButtonAsync'

export const PenyetujuanPesertaDidikList: React.FC<
  IResourceComponentsProps<GetListResponse<any>>
> = ({ options }) => {
  const {
    tableProps,
    searchFormProps,
    filters,
    tableQueryResult: { refetch, isFetching },
  } = useTableWithMeta<
    CalonPesertaDidikResponse,
    TCommonError,
    TFilterForm<CalonPesertaDidikRequest>
  >({
    resource: `diklat/peserta-diklat`,
    dataProviderName: 'lms',
    syncWithLocation: true,
    queryOptions: {
      enabled: false,
    },
    onSearch: (params) => {
      const filters: CrudFilters = []
      const {
        programId,
        programLocalId,
        approvalStatus,
        kodeKecamatan,
        kodeKotaKab,
        kodeProvinsi,
        email,
        npsn,
      } = params
      const shouldAddTimestamp = [
        programId,
        programLocalId,
        approvalStatus,
        kodeKecamatan,
        kodeKotaKab,
        kodeProvinsi,
        email,
        npsn,
      ].some(Boolean)
      filters.push(
        { field: 'page', operator: 'eq', value: 1 },
        {
          field: 'approvalStatus',
          operator: 'eq',
          value: approvalStatus,
        },
        {
          field: 'email',
          operator: 'eq',
          value: email,
        },
        {
          field: 'npsn',
          operator: 'eq',
          value: npsn,
        },
        {
          field: 'programId',
          operator: 'eq',
          value: programId,
        },
        {
          field: 'programLocalId',
          operator: 'eq',
          value: programId ? programLocalId : undefined,
        },
        {
          field: 'kodeProvinsi',
          operator: 'eq',
          value: kodeProvinsi,
        },
        {
          field: 'kodeKotaKab',
          operator: 'eq',
          value: kodeProvinsi ? kodeKotaKab : undefined,
        },
        {
          field: 'kodeKecamatan',
          operator: 'eq',
          value: kodeProvinsi && kodeKotaKab ? kodeKecamatan : undefined,
        },
        {
          field: 't',
          operator: 'eq',
          value: shouldAddTimestamp ? new Date().getTime() : undefined,
        },
      )

      return filters
    },
  })

  /** Only fetch the table when the filter list is changed */
  useEffect(() => {
    filters?.length && refetch()
  }, [filters, refetch])

  const { mutateAsync } = useCustomMutation<
    {},
    TCommonError,
    { status: 'ADMIN_APPROVED' | 'ADMIN_REJECTED'; rejectReason?: string }
  >()

  const doApproval = async (
    id: string,
    status: 'ADMIN_APPROVED' | 'ADMIN_REJECTED',
    rejectReason?: string,
  ) => {
    return await mutateAsync(
      {
        dataProviderName: 'lms',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/diklat/peserta-diklat/${id}/status`,
        method: 'patch',
        values: { status, rejectReason },
        successNotification: {
          message: 'Status approval berhasil disimpan',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (err) =>
          showErrorNotification(
            err,
            'Terdapat gangguan ketika mengubah status approval',
          ),
      },
      {
        onSettled: () => {
          refetch()
        },
      },
    )
  }
  const { show, ...updateProgramLocalModalProps } = useUpdateProgramLocalModal()
  const { show: showRejectModal, ...rejectReasonModalProps } =
    useRejectReasonModal()
  const filterProgramId = Form.useWatch('programId', searchFormProps.form)
  const filterKodeProvinsi = Form.useWatch('kodeProvinsi', searchFormProps.form)
  const filterKodeKotaKab = Form.useWatch('kodeKotaKab', searchFormProps.form)
  const filterEmail = Form.useWatch('email', searchFormProps.form)
  const filterNpsn = Form.useWatch('npsn', searchFormProps.form)

  return (
    <List
      title={
        <>
          Penyetujuan Peserta Didik (<i>Approval</i>)
        </>
      }
    >
      <Head>
        <title>LMS Admin | {options?.label}</title>
      </Head>

      <Collapse
        className="!mb-4"
        bordered={false}
        defaultActiveKey={filters?.length ? undefined : 'filter'}
      >
        <Collapse.Panel
          key={'filter'}
          header={
            <>
              {filters?.length ? (
                <Icons.FilterFilled
                  className="mr-1"
                  style={{ color: '#08c' }}
                />
              ) : (
                <Icons.FilterOutlined className="mr-1" />
              )}
              <TextField
                value="Filter Daftar Calon Peserta"
                strong={Boolean(filters?.length)}
              />
            </>
          }
        >
          <Filter
            formProps={searchFormProps}
            filters={filters as LogicalFilter[]}
            layout="vertical"
            size="small"
            className="!grid !grid-cols-2 gap-x-4 gap-y-0"
            items={[
              {
                label: 'Program',
                name: 'programId',
                required: !filterEmail && !filterNpsn,
                dependencies: ['email', 'npsn'],
                rules: [
                  {
                    required: !filterEmail && !filterNpsn,
                    message: 'Program harus dipilih',
                  },
                ],
                component: (
                  <SelectFilterAsync
                    resource={'diklat/programs'}
                    placeholder="Pilih Program"
                    allowClear
                    showSearch
                    onChange={() => {
                      searchFormProps?.form?.setFieldsValue({
                        programLocalId: undefined,
                      })
                    }}
                  />
                ),
              },
              {
                label: 'Provinsi',
                name: 'kodeProvinsi',
                component: (
                  <SelectProvinceOptions
                    autoComplete="off"
                    aria-autocomplete="none"
                    showSearch
                    visible
                    allowClear
                    onChange={() => {
                      searchFormProps?.form?.setFieldsValue({
                        kodeKotaKab: undefined,
                        kodeKecamatan: undefined,
                      })
                    }}
                    placeholder="Pilih Provinsi"
                  />
                ),
              },
              {
                label: 'Program Lokal',
                name: 'programLocalId',
                component: (
                  <SelectFilterAsync
                    resource={`diklat/programs/${filterProgramId}/local-programs`}
                    enabledQuery={!!filterProgramId}
                    placeholder="Pilih Program Lokal"
                    disabled={!filterProgramId}
                    allowClear
                    showSearch
                  />
                ),
              },
              {
                label: 'Kota/Kabupaten',
                name: 'kodeKotaKab',
                component: (
                  <SelectCityOptions
                    autoComplete="off"
                    aria-autocomplete="none"
                    showSearch
                    provinceId={filterKodeProvinsi}
                    disabled={!filterKodeProvinsi}
                    allowClear
                    initialValue={
                      searchFormProps?.initialValues?.kodeKotaKab
                        ? {
                            city: {
                              name: searchFormProps.initialValues.kotaKab,
                              id: searchFormProps.initialValues.kodeKotaKab,
                            },
                          }
                        : undefined
                    }
                    onChange={() => {
                      searchFormProps?.form?.setFieldsValue({
                        kodeKecamatan: undefined,
                      })
                    }}
                    placeholder="Pilih Kota/Kabupaten"
                  />
                ),
              },
              {
                label: 'Status Pengajuan',
                name: 'approvalStatus',
                component: (
                  <Select
                    allowClear
                    placeholder="Pilih Status"
                    options={[
                      {
                        label: 'Menunggu Persetujuan',
                        value: APPROVAL_STATUS.PENDING,
                      },
                      {
                        label: 'Sudah Disetujui',
                        value: APPROVAL_STATUS.ADMIN_APPROVED,
                      },
                      {
                        label: 'Pengajuan Ditolak',
                        value: APPROVAL_STATUS.ADMIN_REJECTED,
                      },
                    ]}
                  />
                ),
              },
              {
                label: 'Kecamatan',
                name: 'kodeKecamatan',
                component: (
                  <SelectDistrictOptions
                    autoComplete="off"
                    aria-autocomplete="none"
                    showSearch
                    kotaKabId={filterKodeKotaKab}
                    disabled={!filterKodeKotaKab}
                    allowClear
                    initialValue={
                      searchFormProps?.initialValues?.kodeKecamatan
                        ? {
                            district: {
                              name: searchFormProps.initialValues.kecamatan,
                              id: searchFormProps.initialValues.kodeKecamatan,
                            },
                          }
                        : undefined
                    }
                    placeholder="Pilih Kecamatan"
                  />
                ),
              },
              {
                label: 'NPSN',
                name: 'npsn',
                placeholder: 'Cari berdasarkan NPSN peserta',
              },
              {
                label: 'Email',
                name: 'email',
                placeholder: 'Cari berdasarkan email peserta',
              },
            ]}
          />
        </Collapse.Panel>
      </Collapse>
      {!filters?.length && (
        <Alert
          type="warning"
          showIcon
          message="Harap filter program terlebih dahulu, atau filter berdasarkan email/NPSN"
          className="!mb-4"
        />
      )}
      <Table
        {...tableProps}
        bordered
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 400, y: 800 }}
        className="w-full"
        size="small"
        loading={isFetching}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <>
                <Descriptions
                  size="small"
                  column={2}
                  bordered
                  labelStyle={{ width: '180px' }}
                  className="!mb-0"
                  style={{ margin: 0 }}
                  title="Informasi Detail"
                >
                  <Descriptions.Item label="ID" span={2}>
                    {record.id}
                  </Descriptions.Item>
                  <Descriptions.Item label="NPSN">
                    {record.npsn}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nama Sekolah">
                    {record.namaSekolah}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mata Pelajaran">
                    <TextField
                      value={record.mataPelajaran || 'Kosong'}
                      type={!record.mataPelajaran ? 'secondary' : undefined}
                      italic={!record.mataPelajaran}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Dokumen Pendukung">
                    {record.fileUrl ? (
                      <a
                        href={record.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icons.LinkOutlined /> Lihat Dokumen
                      </a>
                    ) : (
                      <TextField value="Kosong" type="secondary" italic />
                    )}
                  </Descriptions.Item>
                </Descriptions>
                {record.approvalStatus === 'ADMIN_REJECTED' && (
                  <Descriptions
                    size="small"
                    column={1}
                    bordered
                    labelStyle={{ width: '180px' }}
                    className="!mb-0 !mt-4"
                    style={{ margin: 0 }}
                    title="Informasi Penolakan"
                  >
                    <Descriptions.Item label="Alasan Penolakan">
                      {record.rejectReason ? (
                        <TextField value={record.rejectReason} />
                      ) : (
                        <TextField value="Kosong" type="secondary" italic />
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </>
            )
          },
        }}
      >
        <Table.Column
          dataIndex="nama"
          title="Nama Calon Peserta"
          render={(value) => <TextField value={value} />}
          width={200}
          fixed="left"
          className="align-top"
        />
        <Table.Column
          dataIndex="email"
          title="Email"
          render={(value) => <TextField value={value} />}
          width={200}
          className="align-top"
        />
        <Table.Column
          dataIndex="phone"
          title="Nomor HP"
          render={(value) =>
            !!value ? (
              <TextField value={value} />
            ) : (
              <TextField value="Kosong" italic type="secondary" />
            )
          }
          width={200}
          className="align-top"
        />
        <Table.Column
          dataIndex="approvalStatus"
          title="Status Pengajuan"
          render={(value: ApprovalStatus) => <ApprovalStatus status={value} />}
          width={200}
          className="align-top"
        />
        <Table.Column<CalonPesertaDidikResponse>
          title="Program & Program Lokal"
          width={400}
          className="align-top"
          render={(_, record) => (
            <Descriptions
              size="small"
              column={1}
              bordered
              labelStyle={{ width: '130px' }}
            >
              <Descriptions.Item label="Program">
                {record.program.name}
              </Descriptions.Item>
              <Descriptions.Item label="Program Lokal">
                {record.programLocal?.name || (
                  <Space>
                    <Icons.ExclamationCircleFilled className="!text-warning" />
                    <TextField value="Belum diatur" type="secondary" italic />
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
        />
        <Table.Column<CalonPesertaDidikResponse>
          title="Wilayah Peserta"
          width={350}
          className="align-top"
          render={(_, record) => (
            <Descriptions
              size="small"
              column={1}
              bordered
              labelStyle={{ width: '130px' }}
            >
              <Descriptions.Item label="Provinsi">
                {record.provinsi || (
                  <TextField value="Kosong" type="secondary" italic />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Kota/Kabupaten">
                {record.kotaKabupaten || (
                  <TextField value="Kosong" type="secondary" italic />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Kecamatan">
                {record.kecamatan || (
                  <TextField value="Kosong" type="secondary" italic />
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
        />
        <Table.Column<CalonPesertaDidikResponse>
          title="Aksi"
          width={160}
          className="align-top"
          align="center"
          render={(_, record) => {
            const isSelfEnrollmentQuotaFull =
              !!record.isSelfEnrollment && !!record.isQuotaFull

            return (
              <Space direction="vertical">
                <ButtonAsync
                  disabled={
                    record.approvalStatus !== 'PENDING' ||
                    !Boolean(Number(record.programLocal?.id)) ||
                    isSelfEnrollmentQuotaFull
                  }
                  block
                  icon={<Icons.CheckCircleFilled />}
                  type="primary"
                  size="small"
                  onClick={async () =>
                    await doApproval(record.id, 'ADMIN_APPROVED')
                  }
                >
                  Setuju
                </ButtonAsync>
                <Button
                  disabled={record.approvalStatus !== 'PENDING'}
                  block
                  icon={<Icons.CloseCircleFilled />}
                  size="small"
                  danger
                  onClick={() => showRejectModal(record)}
                >
                  Tolak
                </Button>
                <Button
                  block
                  icon={<Icons.EditOutlined />}
                  size="small"
                  disabled={
                    record.approvalStatus !== 'PENDING' ||
                    isSelfEnrollmentQuotaFull
                  }
                  onClick={() => show(record)}
                >
                  Program Lokal
                </Button>
                {isSelfEnrollmentQuotaFull && (
                  <TextField
                    value="Kuota pendaftaran mandiri penuh"
                    type="warning"
                    italic
                  />
                )}
                {!Boolean(Number(record.programLocal?.id)) && (
                  <TextField
                    value="Program lokal kosong"
                    type="warning"
                    italic
                  />
                )}
              </Space>
            )
          }}
        />
      </Table>
      <ModalFormUpdateProgramLocal
        {...updateProgramLocalModalProps}
        onSuccess={() => {
          refetch()
        }}
      />
      <ModalRejectReason
        {...rejectReasonModalProps}
        onConfirm={async (id, reason) => {
          await doApproval(id, 'ADMIN_REJECTED', reason)
        }}
      />
    </List>
  )
}
